import React, { useState } from "react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import heroBg from "../assets/BlogImg/BlogHero.png";
import blog1 from "../assets/BlogImg/blog1.jpg";
import blog2 from "../assets/BlogImg/blog2.webp";
import blog3 from "../assets/BlogImg/blog3.avif";
import blog4 from "../assets/BlogImg/blog4.jpg";
import blog5 from "../assets/BlogImg/blog5.png";
import blog6 from "../assets/BlogImg/blog6.jpg";

const blogPosts = [
  {
    title: "Why Every Business Needs a vCISO",
    desc: "Explore the importance of Virtual CISOs for modern businesses to tackle increasing cybersecurity threats.",
    img: blog1,
    tags: ["cybersecurity", "vCISO"],
    date: "June 5, 2025",
    category: "Security",
  },
  {
    title: "Managed IT Services: Pros & ROI",
    desc: "Understand how Managed IT Services enhance business operations, minimize downtime, and improve ROI.",
    img: blog2,
    tags: ["IT", "managed services"],
    date: "May 28, 2025",
    category: "IT Services",
  },
  {
    title: "How to Prepare for a Cyber Audit",
    desc: "Step-by-step guide to help businesses stay compliant and prepare effectively for cybersecurity audits.",
    img: blog3,
    tags: ["compliance", "audit"],
    date: "May 15, 2025",
    category: "Compliance",
  },
  {
    title: "How to Prepare for a Cyber Audit",
    desc: "Step-by-step guide to help businesses stay compliant and prepare effectively for cybersecurity audits.",
    img: blog4,
    tags: ["compliance", "audit"],
    date: "May 15, 2025",
    category: "Compliance",
  },
  {
    title: "Top 7 Cybersecurity Trends for 2025",
    desc: "Explore the latest threats, technologies, and regulatory updates that will shape the cybersecurity landscape this year.",
    img: blog5,
    tags: ["cybersecurity", "trends", "2025"],
    date: "May 18, 2025",
    category: "Security",
  },
  {
    title: "Why Every Business Needs a vCISO",
    desc: "Understand the growing role of virtual CISOs and how they provide expert-level security leadership at a fraction of the cost.",
    img: blog6,
    tags: ["vCISO", "cyber leadership"],
    date: "May 20, 2025",
    category: "Cyber Strategy",
  },
];

export default function BlogPage() {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredPosts = blogPosts.filter((post) => {
    const query = searchQuery.toLowerCase();
    return (
      post.title.toLowerCase().includes(query) ||
      post.desc.toLowerCase().includes(query) ||
      post.category.toLowerCase().includes(query) ||
      post.tags.some((tag) => tag.toLowerCase().includes(query))
    );
  });

  return (
    <div className="bg-gray-50 text-gray-800">
      {/* Hero */}
      <div className="relative h-[400px]">
        <img src={heroBg} alt="Hero" className="absolute inset-0 w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-center text-white px-4">
          <motion.h1
            className="text-4xl md:text-5xl font-bold mb-4"
            initial={{ opacity: 0, y: -30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            Latest Insights from <span className="text-orange-500">Nadezhda Venture</span>
          </motion.h1>
          <motion.p
            className="text-lg max-w-2xl"
            initial={{ opacity: 0, y: -30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            Stay ahead with our expert-written articles on IT, cybersecurity, compliance, and more.
          </motion.p>
        </div>
      </div>

      {/* Filters/Search */}
      <div className="flex flex-wrap justify-between items-center px-6 md:px-16 py-8 gap-4">
        <h2 className="text-xl font-bold text-orange-500">Explore Blogs</h2>
        <div className="relative">
          <input
            type="text"
            placeholder="Search blogs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="border rounded-full px-4 py-2 pr-10 w-64 focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <Search className="absolute right-3 top-2.5 text-gray-400" size={18} />
        </div>
      </div>

      {/* Blog Cards */}
      <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 px-6 md:px-16 pb-20">
        {filteredPosts.map((post, idx) => (
          <motion.div
            key={idx}
            className="bg-white rounded-xl shadow hover:shadow-lg transition duration-300 overflow-hidden"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: idx * 0.1 }}
          >
            <img src={post.img} alt={post.title} className="w-full h-52 object-cover" />
            <div className="p-5">
              <h3 className="text-lg font-semibold mb-2 text-blue-900">{post.title}</h3>
              <p className="text-gray-600 text-sm mb-4">{post.desc}</p>
              <div className="flex flex-wrap gap-2 text-xs mb-2">
                {post.tags.map((tag, i) => (
                  <span
                    key={i}
                    className="bg-orange-100 text-orange-600 px-2 py-1 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>{post.date}</span>
                <span className="text-purple-600 font-medium">{post.category}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Expert Section */}
      <div className="mt-28 bg-white py-20 rounded-3xl shadow-inner text-center">
        <h2 className="text-3xl font-bold text-blue-900 mb-4">Build Your Dream IT Strategy</h2>
        <p className="text-gray-600 max-w-xl mx-auto mb-10">
          Wherever you’re going on your digital journey, we’re here to help you get there with the right security, cloud, and infrastructure experts.
        </p>
        <div className="flex flex-wrap justify-center gap-6">
          {[
            { name: "Mariya", title: "Security Consultant", img: "https://yt3.googleusercontent.com/8fhji0IT-z53ftWzOy3bxXyULUfI4SJ2dSsRiOdFQZBBjDXf7Yfdv0pDGQgEZzTuG86KhZNb=s900-c-k-c0x00ffffff-no-rj" },
            { name: "Minikay", title: "Cloud Architect", img: "https://media.licdn.com/dms/image/v2/D5622AQFcCM5U2OdWAg/feedshare-shrink_800/B56ZYy_zavHoAg-/0/1744612309637?e=2147483647&v=beta&t=rH6inYNw9u7Gfn1dPKxT3l2sdFibVPWCRV2A3Guc9xs" },
            { name: "Junaid", title: "vCISO", img: "https://images.news18.com/ibnlive/uploads/2025/02/Junaid-Khan-Breaks-Silence-On-Trolling-Over-His-Dance-Steps-In-Loveyapa_-%E2%80%98People-Make-Fun-Of-Me%E2%80%A6-2025-02-a00e21fa931754ab451470b2750c58cb-3x2.jpg" },
            { name: "Deborah", title: "Compliance Expert", img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR1XpEN8jcaqAfU-ecVu7sRGcaAgsipY4NTgQ&s" },
            { name: "Doug", title: "SOC Manager", img: "https://thefinancialtechnologyreport.com/wp-content/uploads/2025/04/Doug-Ricket.jpg" },
          ].map((person, index) => (
            <div key={index} className="w-40 text-center">
              <img
                src={person.img}
                alt={person.name}
                className="w-32 h-32 mx-auto rounded-full object-cover shadow-md"
              />
              <p className="font-semibold text-blue-900 mt-3">{person.name}</p>
              <p className="text-sm text-gray-500">{person.title}</p>
            </div>
          ))}
        </div>

        <div className="mt-10">
         <a href="/Full_Stack_Developer_In_Noida">
  <button className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition">
    See Courses
  </button>
</a>
{/* <a href="/JoinNowFormPopup" className="ml-4">
  <button className="text-green-600 font-semibold hover:underline">
    Become a Pro →
  </button>
</a> */}

        </div>
      </div>
    </div>
  );
}
